/**
 * YouTube Video Optimizer
 * Lazy loads YouTube embeds to prevent render blocking
 * Includes loader display and autoplay functionality
 */

(function() {
  'use strict';

  const YouTubeOptimizer = {
    processedIframes: new WeakSet(),
    
    /**
     * Initialize YouTube optimization
     */
    init: function() {
      this.lazyLoadYouTubeIframes();
    },

    /**
     * Create and show loader element
     */
    createLoader: function(container) {
      const loader = document.createElement('div');
      loader.className = 'youtube-loader';
      loader.innerHTML = `
        <div class="youtube-loader-spinner">
          <div class="spinner"></div>
          <p>Loading video...</p>
        </div>
      `;
      container.appendChild(loader);
      return loader;
    },

    /**
     * Remove loader element
     */
    removeLoader: function(container) {
      const loader = container.querySelector('.youtube-loader');
      if (loader) {
        loader.remove();
      }
    },

    /**
     * Lazy load YouTube iframes using IntersectionObserver
     */
    lazyLoadYouTubeIframes: function() {
      const iframes = document.querySelectorAll('iframe[data-src*="youtube.com"]');
      
      if (iframes.length === 0) {
        return; // No YouTube iframes found
      }

      if ('IntersectionObserver' in window) {
        const iframeObserver = new IntersectionObserver((entries, observer) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const iframe = entry.target;
              
              // Skip if already processed
              if (this.processedIframes.has(iframe)) {
                return;
              }
              
              const container = iframe.parentElement;
              
              // Show loader
              this.createLoader(container);
              
              // Load the iframe
              if (iframe.dataset.src) {
                const src = iframe.dataset.src;
                // Add autoplay parameter if not already present
                const separator = src.includes('?') ? '&' : '?';
                const autoplaySrc = src.includes('autoplay=') ? src : src + separator + 'autoplay=1';
                
                iframe.src = autoplaySrc;
                iframe.removeAttribute('data-src');
                
                // Mark as processed
                this.processedIframes.add(iframe);
                
                // Remove loader when iframe loads
                iframe.addEventListener('load', () => {
                  this.removeLoader(container);
                }, { once: true });
              }
              
              // Stop observing this iframe
              observer.unobserve(iframe);
            }
          });
        }, {
          rootMargin: '50px' // Start loading 50px before entering viewport
        });

        iframes.forEach(iframe => {
          if (!this.processedIframes.has(iframe)) {
            iframeObserver.observe(iframe);
          }
        });
      } else {
        // Fallback for older browsers - load immediately
        iframes.forEach(iframe => {
          // Skip if already processed
          if (this.processedIframes.has(iframe)) {
            return;
          }
          
          const container = iframe.parentElement;
          
          // Show loader
          this.createLoader(container);
          
          if (iframe.dataset.src) {
            const src = iframe.dataset.src;
            const separator = src.includes('?') ? '&' : '?';
            const autoplaySrc = src.includes('autoplay=') ? src : src + separator + 'autoplay=1';
            
            iframe.src = autoplaySrc;
            iframe.removeAttribute('data-src');
            
            // Mark as processed
            this.processedIframes.add(iframe);
            
            // Remove loader when iframe loads
            iframe.addEventListener('load', () => {
              this.removeLoader(container);
            }, { once: true });
          }
        });
      }
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      YouTubeOptimizer.init();
    });
  } else {
    YouTubeOptimizer.init();
  }

  // Expose to global scope for debugging
  window.YouTubeOptimizer = YouTubeOptimizer;
})();
